import Axios from "axios";
import useAnalytics from "hooks/analytics";
import useAuthUser from "hooks/auth-user";
import useEngageStatus from "hooks/useEngageStatus";
import { DateTime } from "luxon";
import React, { useEffect, useLayoutEffect, useState } from "react";
import { NotificationManager } from "react-notifications";
import { UserActionEvent } from "utils/analytics/events";
import { INVALID_APPOINTMENT_DATE_ERROR } from "utils/api-error-types";
import { suppressErrorNotificationForTypes } from "utils/axios-error-notification";
import { getLabType } from "utils/lab-type-helper";
import { getPreSelectedLabType } from "utils/session-storage";
import { Button } from "../../components";
import ClinicList from "../../components/clinic-list";
import useLanguage from "../../hooks/language";
import Appointment, {
    canEdit,
    PREP_BLACKOUT_INTERVAL_IN_HOURS,
} from "../../models/appointment";
import Clinic from "../../models/clinic";
import ENDPOINTS from "../../utils/endpoints";
import PaymentForm from "./payment-form";

interface ScheduleAppointmentModalProps {
    onClose?: () => void;
    onConfirm?: () => void;
    onAppointmentNotAvailable?: (appointmentDate: string) => void;
    appointment?: Appointment;
    isVisible?: boolean;
    modalContentRef?: React.RefObject<HTMLDivElement | null>;
}

const ScheduleAppointmentModal: React.FunctionComponent<ScheduleAppointmentModalProps> = ({
    appointment,
    onClose,
    onConfirm,
    onAppointmentNotAvailable,
    isVisible,
    modalContentRef,
}: ScheduleAppointmentModalProps) => {
    const [authUser] = useAuthUser();
    const { translations } = useLanguage();
    const t = translations.appointmentsSection;
    const [analytics] = useAnalytics();
    const { isEngageParticipationActive } = useEngageStatus(appointment?.id);
    // states

    const [loading, setLoading] = useState<boolean>(false);
    const [isoDate, setIsoDate] = useState<string | null>(null);
    const [showPaymentForm, setShowPaymentForm] = useState<boolean>(false);
    const [clinics, setClinics] = useState<Clinic[]>([]);
    const [selectedClinicID, setSelectedClinicID] = useState<number | null>(
        null
    );
    const [startDate, setStartDate] = useState<string | undefined>(undefined);

    const suppressInvalidAppointmentDateNotification = suppressErrorNotificationForTypes(
        [INVALID_APPOINTMENT_DATE_ERROR]
    );

    //This is in order to reset the form payment state on click outside
    useEffect(() => {
        if (!isVisible) setShowPaymentForm(false);
        else fetchClinics();
    }, [isVisible]);

    useEffect(() => {
        if (!isVisible) return;

        const clinicId = appointment?.clinicId ?? null;
        setSelectedClinicID(clinicId);
    }, [isVisible, appointment?.clinicId]);

    useLayoutEffect(() => {
        if (!showPaymentForm) return;
        if (modalContentRef?.current) {
            modalContentRef.current.scrollTop = 0;
        }
    }, [showPaymentForm, modalContentRef]);

    // Handlers

    const onSubmit = () => {
        if (!appointment) return;

        if (appointment.shouldRetake) {
            bookRetake();
        } else if (canEdit(appointment)) {
            updateAppointment();
        }
    };

    const handleAppointmentAvailabilityError = (error: unknown) => {
        if (
            Axios.isAxiosError(error) &&
            error.response?.data?.type === INVALID_APPOINTMENT_DATE_ERROR
        ) {
            onAppointmentNotAvailable?.(isoDate ?? "");
        }
    };

    // Network
    const fetchClinics = async () => {
        const validPrepPhoneAppointment = authUser?.phoneAppointments.find(
            (pa) =>
                pa.isAboutPrep &&
                pa.linkableType === "appointment" &&
                pa.linkableId === appointment?.id &&
                pa.status !== "REJECTED" &&
                pa.status !== "CANCELED"
        );
        let date;
        if (validPrepPhoneAppointment) {
            date = DateTime.fromISO(validPrepPhoneAppointment.startAt)
                .plus({
                    hour: PREP_BLACKOUT_INTERVAL_IN_HOURS,
                })
                .toISO();
            setStartDate(date);
        } else setStartDate(undefined);

        try {
            const labType = getLabType(
                appointment
                    ? appointment.usesPrivateLab
                    : getPreSelectedLabType()
            );
            const { data } = await Axios.post<Clinic[]>(ENDPOINTS.CLINICS, {
                isEngageAppointment: isEngageParticipationActive,
                originPostalCode: authUser?.zipCode?.replace(/\s+/g, ""),
                labType,
            });
            setClinics(data);
        } catch (error) {
            console.log(error);
            NotificationManager.error(error);
        }
    };

    const updateAppointment = async () => {
        if (!appointment) return;

        setLoading(true);

        try {
            await Axios.put(
                ENDPOINTS.APPOINTMENT(appointment.id),
                {
                    datetime: isoDate,
                    clinicId: selectedClinicID,
                },
                suppressInvalidAppointmentDateNotification
            );

            if (onConfirm) {
                onConfirm();
            }

            analytics
                ?.cdp()
                ?.trackEvent(UserActionEvent.ScreeningAppointmentRescheduled);

            NotificationManager.success(
                translations.notification.rescheduledAppointment
            );
        } catch (error) {
            handleAppointmentAvailabilityError(error);
        } finally {
            setShowPaymentForm(false);
            setLoading(false);
        }
    };

    const bookRetake = async () => {
        if (!appointment) return;

        setLoading(true);

        try {
            await Axios.post(
                ENDPOINTS.BOOK_RETAKE_APPOINTMENT,
                {
                    appointmentToRetakeId: appointment.id,
                    datetime: isoDate,
                    clinicId: selectedClinicID,
                },
                suppressInvalidAppointmentDateNotification
            );
            if (onConfirm) onConfirm();
            NotificationManager.success(
                translations.notification.createdAppointment
            );
        } catch (error) {
            handleAppointmentAvailabilityError(error);
        } finally {
            setShowPaymentForm(false);
            setLoading(false);
        }
    };

    // Rendering

    const hasToPay =
        appointment &&
        !appointment.shouldRetake &&
        !appointment.canRescheduleFreely;

    return (
        <div>
            {!showPaymentForm ? (
                <ClinicList
                    clinics={clinics}
                    onDateSelect={setIsoDate}
                    selectedClinicID={selectedClinicID}
                    setSelectedClinicID={setSelectedClinicID}
                    appointment={appointment}
                    startDate={startDate}
                    isDisplayedInModal={true}
                    isModalOpen={isVisible}
                    scrollContainerRef={modalContentRef}
                />
            ) : (
                <PaymentForm
                    appointment={appointment}
                    datetime={isoDate}
                    clinicId={selectedClinicID}
                    onCompletePayment={onConfirm}
                    onClose={() => setShowPaymentForm(false)}
                />
            )}
            <br />
            <div className="dual-button-holder">
                {!hasToPay && (
                    <>
                        <Button onClick={onClose} type="secondary">
                            {t.scheduleModal.exitButtonText}
                        </Button>
                        <Button
                            loading={loading}
                            disabled={!isoDate || !selectedClinicID}
                            onClick={onSubmit}
                        >
                            {t.scheduleModal.confirmButtonText}
                        </Button>
                    </>
                )}
                {hasToPay && !showPaymentForm && (
                    <>
                        <Button onClick={onClose} type="secondary">
                            {t.scheduleModal.exitButtonText}
                        </Button>
                        <Button
                            disabled={!isoDate || !selectedClinicID}
                            loading={loading}
                            onClick={() => setShowPaymentForm(true)}
                        >
                            {t.scheduleModal.payButtonText}
                        </Button>
                    </>
                )}
            </div>
        </div>
    );
};

export default ScheduleAppointmentModal;
